import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const HistoricalAnalysisTab = ({ dateRange }) => {
  const businessHealthData = [
    { month: 'Jan', health: 72, strategy: 65, financial: 78, goals: 60 },
    { month: 'Feb', health: 75, strategy: 68, financial: 80, goals: 62 },
    { month: 'Mar', health: 78, strategy: 70, financial: 82, goals: 64 },
    { month: 'Apr', health: 80, strategy: 73, financial: 85, goals: 65 },
    { month: 'May', health: 82, strategy: 75, financial: 87, goals: 66 },
    { month: 'Jun', health: 85, strategy: 78, financial: 91, goals: 68 }
  ];

  const strategyRefinementData = [
    { month: 'Jan', completed: 4, updated: 2, pending: 3 },
    { month: 'Feb', completed: 5, updated: 3, pending: 1 },
    { month: 'Mar', completed: 6, updated: 2, pending: 1 },
    { month: 'Apr', completed: 6, updated: 3, pending: 0 },
    { month: 'May', completed: 7, updated: 2, pending: 0 },
    { month: 'Jun', completed: 7, updated: 3, pending: 0 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-md p-3 elevation-lg">
          <p className="text-sm font-medium text-foreground font-body mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground font-caption">
                {entry?.name}:
              </span>
              <span className="font-medium text-foreground font-body">
                {entry?.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Business Health Evolution */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Activity" size={18} className="md:w-5 md:h-5" color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              Business Health Evolution
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Track performance metrics across all modules over time
            </p>
          </div>
        </div>
        <div className="w-full h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={businessHealthData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'Source Sans 3' }} className="md:text-xs" />
              <Line
                type="monotone"
                dataKey="health"
                stroke="var(--color-primary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-primary)', r: 3 }}
                name="Health Index"
              />
              <Line
                type="monotone"
                dataKey="strategy"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-secondary)', r: 2 }}
                name="Strategy"
              />
              <Line
                type="monotone"
                dataKey="financial"
                stroke="var(--color-accent)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-accent)', r: 2 }}
                name="Financial"
              />
              <Line
                type="monotone"
                dataKey="goals"
                stroke="var(--color-warning)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-warning)', r: 2 }}
                name="Goals"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Strategy Refinement Tracking */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Grid3x3" size={18} className="md:w-5 md:h-5" color="var(--color-secondary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              Strategy Refinement Tracking
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Business Model Canvas completion and update history
            </p>
          </div>
        </div>
        <div className="w-full h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={strategyRefinementData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '10px', fontFamily: 'Source Sans 3' }}
                className="md:text-xs"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '10px', fontFamily: 'Source Sans 3' }} className="md:text-xs" />
              <Bar dataKey="completed" fill="var(--color-success)" name="Completed" />
              <Bar dataKey="updated" fill="var(--color-warning)" name="Updated" />
              <Bar dataKey="pending" fill="var(--color-error)" name="Pending" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Goal Achievement Timeline */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Target" size={18} className="md:w-5 md:h-5" color="var(--color-accent)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
              Goal Achievement Timeline
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
              Quarterly milestone progress and completion rates
            </p>
          </div>
        </div>
        <div className="space-y-3 md:space-y-4">
          {[
            { quarter: 'Q1 2026', achieved: 85, target: 100, status: 'completed' },
            { quarter: 'Q2 2026', achieved: 68, target: 100, status: 'in-progress' },
            { quarter: 'Q3 2026', achieved: 0, target: 100, status: 'upcoming' },
            { quarter: 'Q4 2026', achieved: 0, target: 100, status: 'upcoming' }
          ]?.map((goal, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs md:text-sm font-medium text-foreground font-body">
                  {goal?.quarter}
                </span>
                <span className="text-xs md:text-sm text-muted-foreground font-caption">
                  {goal?.achieved}% / {goal?.target}%
                </span>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-smooth ${
                    goal?.status === 'completed'
                      ? 'bg-success'
                      : goal?.status === 'in-progress' ?'bg-warning' :'bg-muted'
                  }`}
                  style={{ width: `${goal?.achieved}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HistoricalAnalysisTab;